<template>
  <div class="modern-login-container">
    <div class="login-wrapper">
      <LeftSection />
      <div class="right-section">
        <!-- Loading state while initializing app -->
        <div v-if="showAppLoading" class="app-loading">
          <div class="loading-progress">
            <h3>{{ loadingSteps[currentStep] || 'Loading...' }}</h3>
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
            </div>
            <p class="progress-text">{{ progressPercentage }}%</p>
          </div>
        </div>

        <!-- Checking session -->
        <div v-else-if="loading" class="session-check">
          <div class="loader-icon">
            <svg class="spinner-svg" viewBox="0 0 50 50">
              <circle class="spinner-circle" cx="25" cy="25" r="20" fill="none" stroke="#2dbdb6" stroke-width="2" />
            </svg>
          </div>
          <h2>Checking session...</h2>
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="error-state">
          <h2>Authentication Error</h2>
          <p class="error-message">{{ error }}</p>
          <p>Redirecting to login portal...</p>
        </div>

        <!-- Default: redirecting to portal -->
        <div v-else class="redirect-state">
          <h2>Redirecting to login...</h2>
          <p>You will be redirected to the authentication portal.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LeftSection from '../components/Auth/LeftSection/LeftSection.vue';
import portalAuthService from '../js/portalAuthService';

export default {
  name: 'Login',
  components: {
    LeftSection,
  },
  data() {
    return {
      loading: true,
      showAppLoading: false,
      currentStep: 0,
      progressPercentage: 0,
      loadingSteps: [
        'Loading configuration',
        'Setting up base maps',
        'Configuring parameters',
        'Loading risk data',
        'Initializing weather maps',
        'Processing features',
        'Loading risk maps',
        'Finalizing setup',
      ],
      error: null,
    };
  },
  computed: {
    serverRoute() {
      return this.$store.getters.serverRoute;
    },
  },
  async mounted() {
    const session = await portalAuthService.checkSession();

    this.loading = false;

    if (session.authenticated) {
      await this.initializeApp();
    } else {
      portalAuthService.redirectToLogin(window.location.origin);
    }
  },
  methods: {
    async initializeApp() {
      try {
        this.showAppLoading = true;
        this.currentStep = 0;
        this.progressPercentage = 0;

        const steps = [
          { label: 'Loading configuration', action: () => this.fetchData('/config'), storeName: 'updateConfig' },
          { label: 'Setting up base maps', action: () => this.fetchData('/baseMaps'), storeName: 'updateBaseMapConfig' },
          { label: 'Configuring parameters', action: () => this.fetchData('/parameterconfig'), storeName: 'updateParameterConfig' },
          { label: 'Loading risk data', action: () => this.fetchData('/riskdata'), storeName: 'updateRiskdata' },
          { label: 'Initializing weather maps', action: () => this.fetchData('/weathermaps'), storeName: 'updateWeatherMaps' },
          { label: 'Processing features', action: () => this.fetchData('/features'), storeName: 'updateFeatures', conditional: true, nonFatal: true },
          { label: 'Loading risk maps', action: () => this.fetchData('/maps'), storeName: 'updateRiskMaps' },
          { label: 'Finalizing setup' },
        ];

        this.loadingSteps = steps.map((step) => step.label);

        for (let i = 0; i < steps.length; i++) {
          const step = steps[i];
          this.updateLoadingProgress(i);

          if (!step.action) {
            continue;
          }

          if (step.conditional && !this.$store.getters.config.features) {
            continue;
          }

          // Non-fatal steps (e.g. features) shouldn't block initialization.
          // A 500 here usually means the feature file is missing on S3; we
          // surface the backend message in the Features panel (LayerControls)
          // and let the user continue into the app.
          if (step.nonFatal) {
            try {
              const data = await step.action();
              this.$store.dispatch(step.storeName, data);
              this.$store.dispatch('updateFeaturesError', '');
            } catch (stepError) {
              console.error(`Non-fatal error during "${step.label}" step:`, stepError);
              this.$store.dispatch('updateFeaturesError', stepError.message);
            }
            continue;
          }

          const data = await step.action();
          this.$store.dispatch(step.storeName, data);
        }

        // Set user profile from portal session data
        const userProfile = await portalAuthService.getProfile();
        if (userProfile) {
          this.$store.dispatch('login', userProfile);
        }

        const personalizedData = await this.fetchData('/personalize');
        const normalizedPersonalizedData = typeof personalizedData === 'string'
          ? JSON.parse(personalizedData)
          : personalizedData;
        this.$store.dispatch('personalize', normalizedPersonalizedData);

        this.$store.dispatch('updateInitialData');
        this.showAppLoading = false;
        this.$router.replace({ name: 'map' });
      } catch (e) {
        console.error('App initialization error:', e);
        this.error = `Failed to initialize application: ${e.message}`;
        this.showAppLoading = false;
        this.$store.dispatch('logout');
      }
    },

    async fetchData(endpoint) {
      const response = await portalAuthService.fetch(`${this.serverRoute}${endpoint}`);

      if (!response.ok) {
        let errorMessage = `Failed to load ${endpoint}`;
        
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (e) {
          // Fallback if response is not JSON
          errorMessage = `${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
      
      return response.json();
    },

    updateLoadingProgress(stepIndex) {
      this.currentStep = stepIndex;
      this.progressPercentage = Math.round(
        ((stepIndex + 1) / this.loadingSteps.length) * 100,
      );
    },
  },
};
</script>

<style scoped>
.modern-login-container {
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #233845 0%, #1a2a35 100%);
  padding: 0;
  margin: 0;
}

.login-wrapper {
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  animation: slideIn 0.5s ease-out;
}

.right-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #e0e0e0;
}

.session-check,
.error-state,
.redirect-state {
  text-align: center;
}

.session-check h2,
.error-state h2,
.redirect-state h2 {
  color: #e0e0e0;
  margin-bottom: 1rem;
}

.error-message {
  color: #ff6b6b;
  margin-bottom: 1rem;
}

.app-loading {
  text-align: center;
  width: 80%;
  max-width: 400px;
}

.app-loading h3 {
  color: #2dbdb6;
  margin-bottom: 1rem;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #2dbdb6, #1fa89d);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-text {
  margin-top: 0.5rem;
  color: #999;
  font-size: 0.9rem;
}

.loader-icon {
  margin-bottom: 1rem;
}

.spinner-svg {
  width: 50px;
  height: 50px;
  animation: rotate 1.5s linear infinite;
}

.spinner-circle {
  stroke-dasharray: 90, 150;
  stroke-dashoffset: 0;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

@keyframes rotate {
  100% { transform: rotate(360deg); }
}

@keyframes dash {
  0% { stroke-dasharray: 1, 150; stroke-dashoffset: 0; }
  50% { stroke-dasharray: 90, 150; stroke-dashoffset: -35; }
  100% { stroke-dasharray: 90, 150; stroke-dashoffset: -124; }
}

@keyframes slideIn {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 1024px) {
  .login-wrapper {
    flex-direction: column;
  }
}
</style>
